/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * APIService.ts
 */

import {Service} from "./Service";
import KareviHTTPService, {ProgressWatcher} from "./KareviHTTPService";
import QueryFilter from "../model/pagination/Filters";
import {Entity} from "../model/pagination/Entity";

export interface PageResponse<E extends Entity> {
    content: E[]
    total: number
    count: number
    limit: number
    page: number
}

export default class KareviAPIService<E extends Entity, L> extends Service<E, L> {
    private _httpService?: KareviHTTPService;
    public static id = "APIService"

    constructor(httpService: KareviHTTPService) {
        super();

        this._httpService = httpService;
    }


    /**
     * data contains entity cache
     * @type {Map<String, E>}
     */
    public readonly cacheData: Map<String, E> = new Map<String, E>()

    /**
     * invalidate
     */
    public invalidate = () => {
        this.cacheData.clear()
    }

    public write = (entity: E) => {
        this.cacheData.set(entity.id, entity)
    }


    /**
     * get httpService
     * @return {KareviHTTPService}
     */
    public get httpService(): KareviHTTPService {
        if (!this._httpService) {
            throw new Error('HTTPService not initialized! \nDid you forget to call super.start() in any class extending APIService?');
        }

        return this._httpService;
    }

    /**
     * getOne
     * @description Fetches one T typed resource from API
     * @param endpoint
     * @return {Promise<T>}
     */
    protected async getOne<T>(endpoint: string): Promise<T> {
        let data = await this.httpService.get(endpoint);

        if (!(data.content instanceof Object) ||
            (data.content instanceof Array)) {
            console.error(data)
            throw new Error('Received data does not match expected');
        }

        return data.content as T;
    }

    async getPage<E extends Entity, M = {}>(endpoint: string, filters: QueryFilter<M>): Promise<PageResponse<E>> {
        if (filters) {
            endpoint = `${endpoint}?${filters.serialize()}`;
        }

        return await this.httpService.get(endpoint) as PageResponse<E>;
    }

    /**
     * getList
     * @description Fetches a list of T typed resources from API
     * @param endpoint
     * @param filters
     * @return {Promise<Array>}
     */
    protected async getList<T, M = {}>(endpoint: string, filters?: QueryFilter<M>): Promise<Array<T>> {
        if (filters) {
            endpoint = `${endpoint}?${filters.serialize()}`;
        }

        let data = await this.httpService.get(endpoint);

        if (!(data.content instanceof Array)) {
            throw new Error('Received data does not match expected');
        }

        return data.content;
    }

    /**
     * getBlob
     * @description Fetches a raw binary result from API. Ideal for pictures and other raw files
     * @param endpoint
     * @return {Promise<Blob>}
     */
    protected async getBlob(endpoint: string): Promise<Blob | undefined> {
        let data = await this.httpService.getRaw(endpoint);
        return new Blob(data);
    }

    /**
     * post
     * @description Creates one resource
     * @param endpoint
     * @param payload
     * @param progressWatcher
     * @return {Promise<T>}
     */
    protected async post<T>(endpoint: string, payload: any, progressWatcher?: ProgressWatcher): Promise<T> {
        let curatedPayload;
        if (payload instanceof FormData) {
            console.error("formdata");
            curatedPayload = payload;
        }
        if (payload instanceof Map) {
            console.error("map");
            curatedPayload = Array.from(payload).reduce((obj, [key, value]) => (
                Object.assign(obj, {[key]: value}) // Be careful! Maps can have non-String keys; object literals can't.
            ), {});
        } else {
            console.error("else");
            curatedPayload = payload;
        }
        let data = await this.httpService.post(endpoint, curatedPayload, progressWatcher);

        if (!(data.content instanceof Object) ||
            (data.content instanceof Array)) {
            throw new Error('Received data does not match expected');
        }

        return data.content as T;
    }

    /**
     * post file
     * @description Creates one resource
     * @param endpoint
     * @param payload
     * @param progressWatcher
     * @return {Promise<T>}
     */
    protected async postFile<T>(endpoint: string, payload: Map<string, any> | FormData, progressWatcher?: ProgressWatcher): Promise<T> {
        let data = await this.httpService.post(endpoint, payload, progressWatcher);

        if (!(data.content instanceof Object) ||
            (data.content instanceof Array)) {
            throw new Error('Received data does not match expected');
        }

        return data.content as T;
    }

    /**
     * put
     * @description Updates one resource
     * @param endpoint
     * @param payload
     * @return {Promise<T>}
     */
    protected async put<T>(endpoint: string, payload: any): Promise<T> {
        let data = await this.httpService.put(endpoint, payload);
        if (!(data.content instanceof Object) ||
            (data.content instanceof Array)) {
            throw new Error('Received data does not match expected');
        }

        return data.content as T;
    }

    /**
     * delete
     * @description Deletes one resource
     * @param endpoint
     * @return {Promise<Boolean>}
     */
    protected async delete<T>(endpoint: string): Promise<Boolean> {
        return await this.httpService.delete(endpoint);
    }
}